import * as React from "react";
import classNames from "classnames";
import { Password } from "@bizform/primereact/components/password/Password";
import { Button } from "@bizform/primereact/components/button/Button";
import { PasswordProps } from "@bizform/primereact/password";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import { DataType } from "@bizform/type/model/model/DataType.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { ComponentHelper, Debug, ObjectHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


type Props = PasswordProps & {
};

type WrappedCompState = {
    // attribute
    value?: string;
    tabindex?: number;
    readOnly?: boolean;
    disabled?: boolean;
    promptLabel?: string;
    weakLabel?: string;
    mediumLabel?: string;
    strongLabel?: string;
    feedback?: boolean;
    tooltipAttribute?: TooltipOptions & {text?: string};
    printStyle?: object;
    printClassName?: string;
    passwordtoggler?: boolean;
    // event
    onChange?(e: React.FormEvent<HTMLInputElement>): void;
};

type WrappedFormState = {
    onChange?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
    showPassword: boolean;
};

/**
 * @since 0.0.1
 * @author Byungmin Choi
 * @class BfPassword
 * @description BfPassword component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<string>>()
export class BfPassword extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_1Tcfn98sdP9UTz08ST3QH4HDuKT0mT9",
        category: ComponentCategory.INPUT,
        label: I18n.componentElement.password.label,
        description: I18n.componentElement.password.description,
        icon: "",
        dataType: [DataType.STRING],
        property: null
    };

    focus(): void {
        if (this._password) {
            this._password.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, React.FormEvent<HTMLInputElement>>(
                property,
                this);
                newState.wrappedCompState.feedback = newState.wrappedCompState.feedback ? true : false;
                this.setState(newState, this._resetPanel);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: string): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: string): void {
        if (ComponentHelper.isNull(value)) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: ""
                }
            };
            this.setState(newState);
        } else if ( typeof value === "object") {
            this.emitSubscriptionReject();
        } else {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value
                }
            };
            this.setState(newState);
        }
    }

    @ReactComponentElementInit(BfPassword)
    static init(): ComponentElement<string> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _password: any;
    private _passwordRef: any;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            showPassword: false,
            wrappedCompState: {
                value: ""
            },
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this.onTogglePassword = this.onTogglePassword.bind(this);
        this._setRef = this._setRef.bind(this);
    }

    componentDidUpdate(prevProps: Readonly<PasswordProps & {}>, prevState: Readonly<BaseStateModel & { wrappedCompState: WrappedCompState; showPassword: boolean }>, snapshot?: any): void {
        if (this._password) {
            if (!this.state.wrappedCompState.passwordtoggler && !this.accessibility) {
                this._password.setAttribute("type", "password");
                this.setState({showPassword: false});
            }
            /* else if (this.accessibility) {
	    	    this._password.setAttribute("type", "text");
                this.setState({showPassword: true});
	        } */
            if (this.state.wrappedCompState.tabindex !== this.___tabindex) {
                ComponentHelper.tabindex(this.displayMode, this._password.parentElement.querySelector("button"), this.state.wrappedCompState.tabindex);
                this.___tabindex = this.state.wrappedCompState.tabindex;
            }
        }
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    componentWillUnmount(): void {
        if (this._passwordRef && this._passwordRef.panel) {
            this._passwordRef.panel.remove();
        }
        this._password = null;
        this._passwordRef = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    private _onChangeEvent(event): void {
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: event.target.value
            }
        };
        this.setState(newState, this._callbackSetState);
    }

    onTogglePassword(): void {
        this.setState((prevState) => ({showPassword: !prevState.showPassword}),
            function() {
             if (this.state.showPassword) {
                 this._password.setAttribute("type", "text");
             } else {
                 this._password.setAttribute("type", "password");
             }
        });
    }

    private _callbackSetState() {
        this.emit(this.state.wrappedCompState.value);
    }

    private _resetPanel() {
        if (this._passwordRef && this._passwordRef.meter) {
            this._passwordRef.onKeyup({target: this._passwordRef.inputEl});
        }
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: Props): React.ReactNode {
        delete newProps.passwordtoggler;
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        const key = ComponentHelper.tooltipProps(newProps, this.elementId, this.accessibility);
        const toggler = !this.accessibility ? this._createToggler() : null;
        newProps.feedback = !this.accessibility ? newProps.feedback : false;
        return (
            <div className="p-inputgroup">
                <Password
                    {...newProps}
                    key={key}
                    id={this.elementId}
                    ref={this._setRef}
                    // style={this.accessibility ? {color: "rgba(255,255,255, 0)"} : {}}
                />
                {toggler}
            </div>
        );
    }

    private _createToggler(): React.ReactNode {
        const { passwordtoggler } = this.state.wrappedCompState;
        if (passwordtoggler) {
            return (
                <Button
                    key={"Button_" + this.elementId}
                    icon={
                        classNames(
                        "pi ",
                        { "pi-eye": this.state.showPassword },
                        { "pi-eye-slash": !this.state.showPassword }
                    )}
                    onClick={this.onTogglePassword}
                />
            );
        } else {
            return null;
        }
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };

        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readOnly: true
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };

        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        const { value } = this.state.wrappedCompState;
        let data = "";
        if (this.state.showPassword) {
            data = value;
        } else {
            for (let word of value) {data += "*"; }
        }
        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {...this.state.wrappedCompState.printStyle} : null} className={`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{data}</div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            this._passwordRef = ref;
            this._password = ref.inputEl;
            this.readyComponent();
        }
    }
}
